import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { SalemethodService } from 'src/app/services/api/salemethod/salemethod.service';
import { ToolsService } from 'src/app/services/tools/tools.service';
import { CssService } from 'src/app/services/css-service';

import { PointOfSaleSummary } from 'src/app/services/api/fulle/fulle-pointOfSale/fulle-pointOfSale.service.interface';
import { SaleMethodSlim } from './fulle-selection-SM.interface';
import { FullePointOfSaleService } from 'src/app/services/api/fulle/fulle-pointOfSale/fulle-pointOfSale.service';
import { FulleSaleMethodService } from 'src/app/services/api/fulle/fulle-saleMethod/fulle-saleMethod.service';
import { NextAvailableSlotInfo } from 'src/app/services/api/fulle/fulle-saleMethod/fulle-saleMethod.interface';
import { LogoAndBannerService } from 'src/app/services/fulle/logoAndBanner/logoAndBanner.service';
import { MatDialog } from '@angular/material/dialog';
import { FulleSelectSlotDialogComponent } from '../../dialogs/fulle/fulle-select-slot/fulle-select-slot.component';
import { SelectSlotDialogData } from '../../dialogs/fulle/fulle-select-slot/fulle-select-slot.interface';
import { OrderContext } from '../fulle-product/fulle-product.interface';

@Component({
  selector: 'app-fulle-selection-sm',
  templateUrl: './fulle-selection-SM.component.html',
})
export class FulleSelectionSMComponent implements OnInit, OnDestroy {
  loader = true;
  loaderMessage: string | null = null;

  loaderpop = false;
  statusMessage: string | null = null;
  loaderTransparent = false;

  currPos: PointOfSaleSummary | null = null; // <= ici summary suffit pour banner/img/name
  pointsOfSale: PointOfSaleSummary[] = [];

  saleMethods: SaleMethodSlim[] = [];

  private subSm?: Subscription;

  // params
  merchantName = '';
  webshopId = '';

  nextSlotBySmId: Record<number, NextAvailableSlotInfo> = {};

  constructor(
    private dialog: MatDialog,
    private fullePosApi: FullePointOfSaleService,
    private fulleSMApi: FulleSaleMethodService,
    private logoAndBannerService: LogoAndBannerService,
    private posApi: FullePointOfSaleService,
    private route: ActivatedRoute,
    private router: Router,
    public tools: ToolsService,
    public cssService: CssService,
  ) { }

  ngOnInit(): void {
    const p = this.route.snapshot.params;
    this.merchantName = p['merchantName'];
    this.webshopId = p['webshopId'];

    this.initContext();
  }

  private async initContext(): Promise<void> {
    this.loader = true;
    this.loaderMessage = 'Chargement...';

    try {
      // 1) si j'arrive de FullePointofsaleComponent par selectPOS, j'ai déjà id_merchant en storage
      let idMerchant = this.tools.getStor('id_merchant');

      // 2) si on ne l'a pas on throw une erreur
      if (!idMerchant) throw new Error('id_merchant missing');

      // 3) source de vérité : nouvelle API Slim → liste des POS
      this.pointsOfSale = await this.fullePosApi.getPointOfSaleSummary(idMerchant, '', 'timetables');

      // 4) choisir le POS courant en fonction du webshopId de l’URL
      const pos = (this.pointsOfSale ?? []).find(x => String(x.idWebshop) === String(this.webshopId));
      if (!pos) {
        this.router.navigate(['/404', 'Urlincomplet']);
        return;
      }
      this.currPos = pos; // source de vérité si la fonction suivante retourne null
      this.logoAndBannerService.initLogoAndBannerSummary(pos).then((enrichedPoint: PointOfSaleSummary) => {
        if (enrichedPoint) {
          this.currPos = enrichedPoint;
        }
      });

      // 5) on set le Point au cas ou se soit la version non slim en storage.
      this.posApi.setPoint(pos);

      // 6) puis je charge les SM
      this.loadSaleMethods();

      this.loader = false;
      this.loaderMessage = null;
    } catch (e) {
      this.router.navigate(['/404', 'Urlincomplet']);
    }
  }

  ngOnDestroy(): void {
    if (this.subSm) this.subSm.unsubscribe();
  }

  private loadSaleMethods(): void {
    if (this.subSm) this.subSm.unsubscribe();
    const idMerchant = this.tools.getStor('id_merchant');
    this.fulleSMApi.getSaleMethods(idMerchant, '', '').then((data: SaleMethodSlim[]) => {
      // Filtrage + tri stable
      this.saleMethods = (data ?? [])
        .filter(sm => this.isDisplayed(sm))
        .sort((a, b) => a.name.localeCompare(b.name));

      // on charge les prochains creneaux
      this.computeNextSlots();
      this.loader = false;
      this.loaderMessage = null;

      this.autoSelectIfSingle();
    })
  }

  private autoSelectIfSingle(): void {
    if (this.saleMethods.length !== 1) return;

    const sm = this.saleMethods[0];
    if (!this.canSelect(sm)) return;

    void this.selectSm(sm);
  }

  isDisplayed(sm: SaleMethodSlim): boolean {
    if (sm.webshopVisible !== true) return false;
    return true;
  }

  canSelect(sm: SaleMethodSlim): boolean {
    if (!this.isDisplayed(sm)) return false;

    // exemple : si SM ne permet pas de commander, on bloque
    if (!sm.enableOrder) return false;

    return true;
  }

  async selectSm(sm: SaleMethodSlim): Promise<void> {
    if (!this.canSelect(sm)) return;

    // on stocke la SM choisie
    this.fulleSMApi.setSaleMeth(sm, true);
    this.tools.setStor(SalemethodService.TAG_STORAGE, sm);

    // si pas de date/hour => je continue comme avant 
    if (sm.enableDateHour !== true) {
      this.continueAfterSmChosen(sm);
      return;
    }

    // Je calcule les créneaux au clic
    if (!this.currPos) return;
    const groups = this.fulleSMApi.getAvailableSlots(sm, this.currPos, new Date(), 7);

    // ouvre le dialog
    const ref = this.dialog.open(FulleSelectSlotDialogComponent, {
      width: '600px',
      maxWidth: '100vw',
      height: 'auto',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: ['fulle-slot-dialog'],
      data: { sm, pos: this.currPos, groups } as SelectSlotDialogData,
    });

    const result = await firstValueFrom(ref.afterClosed());

    // annulation si erreur
    if (!result?.start) return;

    this.loader = true;

    // stockage pour le prochain composant
    const d = result.start;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');

    if (this.currPos) {
      const ctx: OrderContext = {
        webshopId: this.currPos.idWebshop ?? this.currPos.idWebshop,
        idMerchant: this.tools.getStor('id_merchant'),
        posId: this.currPos.id,
        saleMethodId: sm.id,
        dateOrder: `${yyyy}-${mm}-${dd} 00:00:00`,
        hourOrder: `${yyyy}-${mm}-${dd} ${hh}:${mi}:00`,
      };
      this.tools.setStor('order_context', ctx);
      this.tools.setStor('date_order', ctx.dateOrder);
      this.tools.setStor('hour_order', ctx.hourOrder);
    } else {
      this.tools.setStor('date_order', `${yyyy}-${mm}-${dd}`);
      this.tools.setStor('hour_order', `${hh}:${mi}:00`);
    }
    this.tools.setStor(SalemethodService.TAG_STORAGE, sm);

    this.continueAfterSmChosen(sm);
  }

  private continueAfterSmChosen(sm: SaleMethodSlim) {
    this.router.navigate(['/s', this.merchantName, this.webshopId, sm.id]);
  }

  computeNextSlots() {
    const now = new Date();
    for (const sm of this.saleMethods) {
      if (sm.enableDateHour !== true) continue;
      if (this.currPos)
      this.nextSlotBySmId[sm.id] =
        this.fulleSMApi.getNextAvailableSlot(sm, this.currPos, now);
    }
  }
}
